import React, { useContext, useEffect } from "react";
import { MenuActive } from "./context/MenuActiveContext";

const PrivacyPolicy = () => {
  const { setMunuActiv } = useContext(MenuActive);

  useEffect(() => {
    setMunuActiv([true, "PrivacyPolicy"]);
  }, [setMunuActiv]);

  const headingClass = "text-xl font-semibold text-gray-900 mt-8 mb-3";
  const subHeadingClass = "text-lg font-semibold text-gray-900 mt-6 mb-2";
  const paragraphClass = "text-gray-600 leading-7 mb-4";
  const listClass =
    "list-disc pl-6 text-gray-600 leading-7 mb-4 space-y-2";

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">

        {/* Header */}
        <div className="border-b border-gray-200 pb-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Privacy Policy
          </h1>

          <p className="text-lg font-medium text-gray-700 mb-2">
            TikiTaka Website, Android App and iOS App
          </p>

          <p className="text-sm text-gray-500">
            Website:{" "}
            <a
              href="https://tikitaka.ae"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline"
            >
              tikitaka.ae
            </a>
            {" | "}
            Email:{" "}
            <a
              href="mailto:info@tikitaka.ae"
              className="text-blue-600 hover:underline"
            >
              info@tikitaka.ae
            </a>
          </p>

          <p className="text-sm text-gray-500 mt-2">
            <strong>Effective Date:</strong> 19 August 2026
          </p>
        </div>

        <p className={paragraphClass}>
          This Privacy Policy explains how TikiTaka (“TikiTaka”, “we”, “us” or
          “our”) collects, uses, stores, discloses and protects personal data
          when you use the TikiTaka website, Android application, iOS
          application, customer account, marketplace features, customer support
          and related services (collectively, the “Services”).
        </p>

        <p className={paragraphClass}>
          TikiTaka is operated by MOUHAMADOU BACHIR AUTO SPARE PARTS &
          COMPONENTS TRADING CO. L.L.C, a company licensed in Dubai, United Arab
          Emirates under Trade Licence No. 990539.
        </p>

        <p className={paragraphClass}>
          TikiTaka is a Dubai-based online marketplace for new and used
          automotive parts and related items. The website supports customer
          accounts and web-based Vendor functionality. The Android and iOS
          applications are intended for Users/Customers and do not provide
          Vendor login or Vendor account-management functionality. Vendor
          accounts and Vendor marketplace tools are available through the
          website only.
        </p>

        <p className={paragraphClass}>
          This Privacy Policy is intended to support transparency under
          applicable UAE data-protection requirements and the privacy
          requirements applicable to apps distributed through the Apple App
          Store and Google Play.
        </p>

        {/* 1 */}
        <h2 className={headingClass}>1. Scope of This Privacy Policy</h2>

        <p className={paragraphClass}>
          This Privacy Policy applies to personal data processed through
          tikitaka.ae, the TikiTaka Android app, the TikiTaka iOS app, customer
          login and registration, web Vendor onboarding and Vendor accounts,
          Orders, marketplace transactions, communications, support requests,
          account deletion requests, and other interactions with TikiTaka.
        </p>

        <p className={paragraphClass}>
          It does not govern independent third-party websites, payment
          providers, logistics providers or other services that have their own
          privacy policies, except to explain when TikiTaka may share data with
          those parties.
        </p>

        {/* 2 */}
        <h2 className={headingClass}>2. Who Uses TikiTaka</h2>

        <p className={paragraphClass}>The Services may be used by:</p>

        <ul className={listClass}>
          <li>Visitors who browse the website or mobile apps;</li>

          <li>
            Customers/Users who register, log in, search for products, place
            Orders, manage profiles or use other customer features;
          </li>

          <li>
            Customer Sellers who may use permitted website marketplace features
            to sell eligible parts or items, where enabled;
          </li>

          <li>
            Vendors who register and access Vendor features through the
            TikiTaka website only; and
          </li>

          <li>
            People who contact TikiTaka for support, complaints, privacy
            requests or other enquiries.
          </li>
        </ul>

        <p className={paragraphClass}>
          There is no Vendor login in the TikiTaka Android or iOS mobile
          applications. Vendor authentication, listings, inventory, Orders and
          other Vendor tools are web-only unless TikiTaka expressly introduces
          such functionality in a future app version.
        </p>

        {/* 3 */}
        <h2 className={headingClass}>3. Personal Data We May Collect</h2>

        <p className={paragraphClass}>
          Depending on how you use the Services, we may collect the following
          categories of information.
        </p>

        <h3 className={subHeadingClass}>
          3.1 Account and Identity Information
        </h3>

        <ul className={listClass}>
          <li>Full name;</li>
          <li>Email address;</li>
          <li>Mobile/telephone number;</li>
          <li>Username, customer identifier or account ID;</li>
          <li>
            Password or authentication credentials in protected/hashed form,
            where applicable;
          </li>
          <li>Profile information and account preferences; and</li>
          <li>
            Verification information where reasonably required for fraud
            prevention, account security or legal compliance.
          </li>
        </ul>

        <h3 className={subHeadingClass}>
          3.2 Customer and Order Information
        </h3>

        <ul className={listClass}>
          <li>Billing and delivery address;</li>
          <li>Products viewed, saved, purchased, returned or disputed;</li>
          <li>Shopping cart, wishlist and Order history;</li>
          <li>
            Vehicle information voluntarily supplied to find compatible parts,
            such as make, model, year, engine details, registration-related
            details or VIN/chassis information where a feature requires it;
          </li>
          <li>Delivery instructions and fulfilment status; and</li>
          <li>
            Invoices, refunds, cancellations and customer-service history.
          </li>
        </ul>

        <h3 className={subHeadingClass}>
          3.3 Seller and Vendor Information - Website Only
        </h3>

        <p className={paragraphClass}>
          Where a person uses Seller or Vendor functions on the website, we may
          collect business and marketplace information such as business name,
          contact person, trade licence or registration information, VAT/tax
          information where applicable, payout details, store information,
          product listings, inventory, pricing, Order records, Seller
          performance information and supporting verification documents.
        </p>

        <p className={paragraphClass}>
          The mobile apps do not provide Vendor login. Therefore, Vendor account
          credentials, Vendor dashboard activity and Vendor listing-management
          data are not collected through a Vendor login flow in the Android or
          iOS apps.
        </p>

        <h3 className={subHeadingClass}>3.4 Payment Information</h3>

        <p className={paragraphClass}>
          Payments may be handled by third-party payment processors or financial
          institutions. TikiTaka may receive transaction identifiers, payment
          status, payment method type, masked card details, refund status and
          fraud/risk signals.
        </p>

        <p className={paragraphClass}>
          We do not intend to store full payment-card numbers or card security
          codes unless expressly required and lawfully implemented through a
          compliant payment environment.
        </p>

        <h3 className={subHeadingClass}>
          3.5 Communications and User Content
        </h3>

        <ul className={listClass}>
          <li>Emails and customer-support messages;</li>
          <li>Complaints, return requests and dispute information;</li>
          <li>
            Reviews, ratings, product questions and other marketplace content;
          </li>
          <li>
            Photographs, videos or documents voluntarily submitted for
            listings, support, returns, verification or disputes; and
          </li>
          <li>
            Records of communications with TikiTaka where permitted by law.
          </li>
        </ul>

        <h3 className={subHeadingClass}>
          3.6 Device, Technical and Usage Data
        </h3>

        <p className={paragraphClass}>
          When you use the website or apps, certain technical information may be
          collected automatically by our systems or service providers, such as
          IP address, device type, operating system, app version, browser type,
          language, timestamps, session information, pages or screens viewed,
          feature interactions, crash information, diagnostics, security logs
          and approximate location inferred from IP address where applicable.
        </p>

        <p className={paragraphClass}>
          If the mobile apps use device identifiers, analytics SDKs,
          crash-reporting SDKs, push-notification services or similar
          technologies, their data collection must be reflected accurately in
          this Privacy Policy, Apple App Privacy disclosures and Google Play
          Data Safety declarations.
        </p>

        <h3 className={subHeadingClass}>3.7 Location Data</h3>

        <p className={paragraphClass}>
          TikiTaka may process delivery addresses and location information that
          you voluntarily provide.
        </p>

        <p className={paragraphClass}>
          If an app feature requests access to device location, we will request
          the relevant device permission where required and use location only
          for the disclosed feature, such as delivery, nearby services or
          location-assisted address selection.
        </p>

        <h3 className={subHeadingClass}>3.8 Camera, Photos and Files</h3>

        <p className={paragraphClass}>
          If you choose features that allow you to upload a profile image,
          product/part image, return evidence, document or support attachment,
          the app or website may request access to your camera, photo library or
          selected files.
        </p>

        <p className={paragraphClass}>
          Access is used only to provide the feature you initiate, subject to
          the permissions available on your device.
        </p>

        <h3 className={subHeadingClass}>
          3.9 Cookies and Similar Technologies
        </h3>

        <p className={paragraphClass}>
          The website may use cookies, local storage, pixels and similar
          technologies for login sessions, security, preferences, shopping-cart
          functionality, analytics and performance.
        </p>

        <p className={paragraphClass}>
          Where required by applicable law, consent mechanisms may be used for
          non-essential cookies.
        </p>

        {/* 4 */}
        <h2 className={headingClass}>4. How We Use Personal Data</h2>

        <ul className={listClass}>
          <li>Create, authenticate, maintain and secure accounts;</li>
          <li>Provide website and mobile-app functionality;</li>
          <li>Process Orders, payments, refunds, returns and deliveries;</li>
          <li>
            Connect Buyers and Sellers and facilitate marketplace transactions;
          </li>
          <li>Provide customer support;</li>
          <li>Display and manage listings, reviews and content;</li>
          <li>
            Verify accounts and detect fraud, abuse and security threats;
          </li>
          <li>Diagnose technical problems and improve performance;</li>
          <li>Personalize relevant features;</li>
          <li>Send transactional and security messages;</li>
          <li>Send permitted marketing communications;</li>
          <li>Comply with legal and regulatory requirements;</li>
          <li>Establish, exercise or defend legal claims; and</li>
          <li>Improve and develop TikiTaka products and Services.</li>
        </ul>

        {/* 5 */}
        <h2 className={headingClass}>5. Legal Grounds and Consent</h2>

        <p className={paragraphClass}>
          We process personal data on legal grounds available under applicable
          UAE law, which may include consent, performance of a contract,
          compliance with legal obligations, protection of legitimate interests
          where permitted, protection of rights and safety, and other grounds
          recognized by applicable law.
        </p>

        <p className={paragraphClass}>
          Where consent is required, you may withdraw it through available
          account/device controls or by contacting info@tikitaka.ae.
        </p>

        {/* 6 */}
        <h2 className={headingClass}>6. How We Share Personal Data</h2>

        <p className={paragraphClass}>
          We may disclose personal data only as reasonably necessary to parties
          such as:
        </p>

        <ul className={listClass}>
          <li>Sellers or Vendors involved in fulfilling a transaction;</li>
          <li>Payment gateways, banks and payment service providers;</li>
          <li>Couriers, logistics providers and delivery partners;</li>
          <li>
            Cloud hosting, infrastructure, security, communications, support,
            analytics and technical service providers;
          </li>
          <li>Professional advisers;</li>
          <li>
            Government authorities, regulators, courts or law-enforcement
            bodies where legally required; and
          </li>
          <li>
            A purchaser or successor in connection with a lawful merger,
            acquisition or sale of business/assets.
          </li>
        </ul>

        {/* 7 */}
        <h2 className={headingClass}>
          7. Seller Access to Customer Information
        </h2>

        <p className={paragraphClass}>
          A Seller may receive customer information necessary to fulfil an
          Order, such as the customer’s name, contact information, delivery
          information and transaction details.
        </p>

        <p className={paragraphClass}>
          Sellers must use such information only for fulfilment, after-sales
          service, legal compliance or another lawful purpose and must not use
          it for unauthorized marketing, resale, profiling or disclosure.
        </p>

        {/* 8 */}
        <h2 className={headingClass}>8. International Data Transfers</h2>

        <p className={paragraphClass}>
          Some technology, hosting, payment, analytics, communications or other
          service providers may process information outside the UAE.
        </p>

        <p className={paragraphClass}>
          Where personal data is transferred internationally, TikiTaka will
          take steps required by applicable UAE data-protection law.
        </p>

        {/* 9 */}
        <h2 className={headingClass}>9. Data Security</h2>

        <p className={paragraphClass}>
          We use reasonable administrative, organizational and technical
          measures designed to protect personal data against unauthorized
          access, alteration, disclosure, loss, misuse or destruction.
        </p>

        <p className={paragraphClass}>
          Measures may include access controls, authentication, encryption in
          transit, secure infrastructure, logging, backups, vulnerability
          management and restricted staff/service-provider access.
        </p>

        {/* 10 */}
        <h2 className={headingClass}>10. Data Retention</h2>

        <p className={paragraphClass}>
          We retain personal data only for as long as reasonably necessary for
          the purposes described in this Privacy Policy, including maintaining
          accounts, completing transactions, providing support, satisfying
          legal requirements, preventing fraud and resolving disputes.
        </p>

        {/* 11 */}
        <h2 className={headingClass}>
          11. Customer Account Deletion - Website, Android and iOS
        </h2>

        <p className={paragraphClass}>
          Customers may request deletion of their TikiTaka account and
          associated personal data.
        </p>

        <p className={paragraphClass}>
          Where the Android or iOS app permits account creation, TikiTaka will
          provide a readily discoverable method in the app to initiate account
          deletion.
        </p>

        <p className={paragraphClass}>
          Customers will also be provided with an external web-based method to
          request account deletion.
        </p>

        <p className={paragraphClass}>
          Users may also contact{" "}
          <a
            href="mailto:info@tikitaka.ae"
            className="text-blue-600 hover:underline"
          >
            info@tikitaka.ae
          </a>{" "}
          for assistance.
        </p>

        {/* 12 */}
        <h2 className={headingClass}>
          12. Vendor Account Deletion - Website Only
        </h2>

        <p className={paragraphClass}>
          Because Vendor login is available only through the website, Vendor
          account-management and deletion requests are handled through the web
          Vendor process and/or by contacting info@tikitaka.ae.
        </p>

        {/* 13 */}
        <h2 className={headingClass}>13. Your Privacy Rights</h2>

        <p className={paragraphClass}>
          Subject to applicable UAE law and relevant exceptions, you may have
          rights concerning your personal data, including rights to:
        </p>

        <ul className={listClass}>
          <li>Obtain information about processing;</li>
          <li>Access personal data;</li>
          <li>Request correction;</li>
          <li>Request deletion where applicable;</li>
          <li>Request restriction of certain processing;</li>
          <li>Receive or transfer data where applicable;</li>
          <li>Object to certain automated processing; and</li>
          <li>Withdraw consent where processing is based on consent.</li>
        </ul>

        {/* 14 */}
        <h2 className={headingClass}>14. Marketing Communications</h2>

        <p className={paragraphClass}>
          Where permitted, TikiTaka may send offers, marketplace updates or
          promotional communications.
        </p>

        <p className={paragraphClass}>
          You may opt out using an unsubscribe mechanism where provided or by
          contacting us.
        </p>

        {/* 15 */}
        <h2 className={headingClass}>15. Push Notifications</h2>

        <p className={paragraphClass}>
          The mobile apps may send push notifications for Orders, delivery
          updates, account activity, marketplace information or promotions where
          enabled.
        </p>

        <p className={paragraphClass}>
          You can control push-notification permissions through app or device
          settings.
        </p>

        {/* 16 */}
        <h2 className={headingClass}>
          16. Analytics, Diagnostics and Third-Party SDKs
        </h2>

        <p className={paragraphClass}>
          TikiTaka may use analytics, crash-reporting, performance,
          authentication, notification, payment or other software development
          kits (“SDKs”) to operate and improve the apps.
        </p>

        <p className={paragraphClass}>
          The exact data collected depends on the SDKs included in the
          production build.
        </p>

        {/* 17 */}
        <h2 className={headingClass}>17. Apple App Store Privacy</h2>

        <p className={paragraphClass}>
          The iOS app is subject to applicable Apple platform privacy
          requirements.
        </p>

        <p className={paragraphClass}>
          TikiTaka will maintain a publicly accessible Privacy Policy URL for
          the app and make privacy information accessible from within the app.
        </p>

        {/* 18 */}
        <h2 className={headingClass}>18. Google Play Data Safety</h2>

        <p className={paragraphClass}>
          The Android app is subject to applicable Google Play User Data and
          Data Safety requirements.
        </p>

        <p className={paragraphClass}>
          TikiTaka will maintain an active, publicly accessible Privacy Policy
          webpage and make it available through applicable Google Play and app
          channels.
        </p>

        {/* 19 */}
        <h2 className={headingClass}>19. Children’s Privacy</h2>

        <p className={paragraphClass}>
          TikiTaka is an automotive-parts marketplace intended for Users capable
          of entering into the relevant transactions.
        </p>

        <p className={paragraphClass}>
          The Services are not designed or directed specifically to young
          children.
        </p>

        {/* 20 */}
        <h2 className={headingClass}>
          20. Automated Decisions and Fraud Prevention
        </h2>

        <p className={paragraphClass}>
          TikiTaka may use automated rules or risk signals to detect suspicious
          logins, fraudulent transactions, abuse or security threats.
        </p>

        {/* 21 */}
        <h2 className={headingClass}>21. Data Breaches</h2>

        <p className={paragraphClass}>
          If a personal-data security incident occurs, TikiTaka will
          investigate, mitigate and make notifications to affected individuals
          or competent authorities where required by applicable law.
        </p>

        {/* 22 */}
        <h2 className={headingClass}>
          22. Third-Party Links and Services
        </h2>

        <p className={paragraphClass}>
          The Services may contain links to third-party websites, maps, payment
          pages, social services or other independent services.
        </p>

        <p className={paragraphClass}>
          Their privacy practices are governed by their own notices.
        </p>

        {/* 23 */}
        <h2 className={headingClass}>
          23. Changes to This Privacy Policy
        </h2>

        <p className={paragraphClass}>
          We may update this Privacy Policy to reflect changes in law,
          technology, marketplace operations, mobile-app functionality, SDKs,
          data practices or service providers.
        </p>

        <p className={paragraphClass}>
          The updated version will display a revised effective date.
        </p>

        {/* 24 */}
        <h2 className={headingClass}>24. Contact Us</h2>

        <p className={paragraphClass}>
          For privacy questions, requests to access, correct or delete personal
          data, account-deletion assistance, complaints or other privacy
          enquiries, contact:
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mt-4">
          <p className="font-semibold text-gray-900 mb-2">TikiTaka</p>

          <p className="text-gray-600 mb-1">
            Operated by: MOUHAMADOU BACHIR AUTO SPARE PARTS & COMPONENTS
            TRADING CO. L.L.C
          </p>

          <p className="text-gray-600 mb-1">
            Trade Licence No.: 990539
          </p>

          <p className="text-gray-600 mb-1">
            Website:{" "}
            <a
              href="https://tikitaka.ae"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline"
            >
              tikitaka.ae
            </a>
          </p>

          <p className="text-gray-600 mb-1">
            Email:{" "}
            <a
              href="mailto:info@tikitaka.ae"
              className="text-blue-600 hover:underline"
            >
              info@tikitaka.ae
            </a>
          </p>

          <p className="text-gray-600">
            Dubai, United Arab Emirates
          </p>
        </div>

        {/* 25 */}
        <h2 className={headingClass}>
          25. Applicable Legal and Platform Requirements
        </h2>

        <p className={paragraphClass}>
          This Privacy Policy is intended to comply with applicable UAE
          data-protection requirements and applicable privacy requirements of
          the Apple App Store and Google Play, as amended or updated from time
          to time.
        </p>

      </div>
    </div>
  );
};

export default PrivacyPolicy;